import { defineComponent as _defineComponent } from 'vue'
import {
  GeoJSONSource,
  MapboxGeoJSONFeature,
  MapMouseEvent,
  EventData,
} from 'mapbox-gl'
import { Feature, GeoJsonProperties, Geometry, Point } from 'geojson'

import { useRoute, useRouter } from 'vue-router/composables'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useGetters } from 'vuex-composition-helpers'
import { groupBy } from 'lodash'

import RightDrawer from '../common/RightDrawer.vue'
import LocationsLayer from './layers/LocationsLayer.vue'
import LocationsModifiedLayer from './layers/LocationsModifiedLayer.vue'
import LocationsSelectedLayer from './layers/LocationsSelectedLayer.vue'
import FieldViewSearch from './FieldViewSearch.vue'
import FieldMap from './mapbox/FieldMap.vue'
import ResourcesLayer from './layers/ResourcesLayer.vue'
import ResourcesSelectedLayer from './layers/ResourcesSelectedLayer.vue'
import JobsLayer from './layers/JobsLayer.vue'
import SingleJobLayer from '@/components/field-view-next/layers/SingleJobLayer.vue'
import FieldViewHoverPopup from './hovers/FieldViewHoverPopup.vue'
import FeedbackDialog from '@/components/field-view-next/feedback/FeedbackDialog.vue'
import LayersMenu from '@/components/field-view-next/LayersMenu.vue'
import NewLocationMenu from '@/components/field-view-next/layers/NewLocationMenu.vue'
import { useCreateFieldViewState } from './FieldViewState'
import { useJobFilterStore } from './JobsFilterStore'
import { useNavStore } from '@/components/nav/navStore'
import { useScreen } from '@/common/useScreen'


export default /*#__PURE__*/_defineComponent({
  __name: 'FieldView',
  setup(__props) {

const route = useRoute()
const router = useRouter()

const showFeedbackDialog = ref<boolean>()

const jobFilterStore = useJobFilterStore()
const navStore = useNavStore()
const { isMobileView } = useScreen()

const { isAdmin } = useGetters('permissions', ['isAdmin'])

const showSideSheet = computed(() => {
  return route.name !== 'field-view-next'
})

const hiddenLocationIds = computed(() => {
  const hidden = state.value.modifiedLocations.map((l) => l.id) || []

  if (state.value.selectedLocation?.id) {
    hidden.push(state.value.selectedLocation?.id)
  }

  return hidden.filter((i) => i !== null) as number[]
})

const selectedLocationIds = computed(() => {
  return state.value.selectedLocation?.id
    ? [state.value.selectedLocation.id]
    : []
})

const hiddenResourceIds = computed(() => {
  return state.value.selectedResource?.id
    ? [state.value.selectedResource.id]
    : []
})

const { state } = useCreateFieldViewState()

const handleLocationsClicked = (
  features: MapboxGeoJSONFeature[],
  eventData: MapMouseEvent & EventData
) => {
  eventData.preventDefault()
  if (features.length === 1) {
    router.push({
      name: 'fvnext-info-panel',
      params: { id: features[0].id!.toString(), source: 'locations' },
    })
  } else {
    router.push({
      name: 'fvnext-picker-panel',
      params: { ids: features.map((f) => f.id).join(','), source: 'locations' },
    })
  }
}

const handleResourcesClicked = (
  features: MapboxGeoJSONFeature[],
  eventData: MapMouseEvent & EventData
) => {
  eventData.preventDefault()

  if (features.length === 1) {
    router.push({
      name: 'fvnext-info-panel',
      params: { id: features[0].id!.toString(), source: 'resources' },
    })
  } else {
    router.push({
      name: 'fvnext-picker-panel',
      params: { ids: features.map((f) => f.id).join(','), source: 'resources' },
    })
  }
}

const handleJobsClustersClicked = (
  features: MapboxGeoJSONFeature[],
  eventData: MapMouseEvent & EventData
) => {
  eventData.preventDefault()
  // should always be one feature due to clustering
  const feature = features[0]
  if (!feature) return

  if (feature.properties?.cluster) {
    const { cluster_id: clusterId, point_count: pointCount } =
      feature.properties
    const source = eventData.target.getSource('jobs') as GeoJSONSource

    source.getClusterLeaves(
      clusterId,
      pointCount,
      0,
      (err, leaves: Feature<Geometry, GeoJsonProperties>[]) => {
        if (err) return

        const grouped = groupBy(leaves, (leaf) => leaf.properties?.LocationId)
        const moreThanOneLocation = Object.keys(grouped).length > 1

        // if a cluster contains jobs for more than one location,
        // and the zoom level has not exceeded 16, zoom into the cluster
        // to break it up.
        if (moreThanOneLocation && eventData.target.getZoom() < 16) {
          source.getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return

            eventData.target.easeTo({
              center: (feature.geometry as Point).coordinates as [
                number,
                number
              ],
              zoom: Math.min(16, zoom),
            })
          })
        } else if (moreThanOneLocation) {
          const jobsGroupedByLocation = Object.keys(grouped).reduce(
            (prev, location) => ({
              ...prev,
              [location]: grouped[location].map((f) => f.properties?.JobNumber),
            }),
            {}
          )
          const ids = JSON.stringify(jobsGroupedByLocation)

          router.push({
            name: 'fvnext-picker-panel',
            params: { ids, source: 'jobs-cluster' },
          })
        } else {
          router.push({
            name: 'fvnext-info-panel',
            params: {
              id: Object.keys(grouped)[0],
              source: 'locations',
            },
          })
        }
      }
    )
  } else {
    router.push({
      name: 'fvnext-info-panel',
      params: { id: feature.properties?.LocationId, source: 'locations' },
    })
  }
}

const handleNewLocationClick = () => {
  router.push({
    name: 'fvnext-new-location',
  })
}

const hideLocationsLayer = computed(() => {
  if (!state.value.layerToggles.locations) {
    return true
  }
})

const hideResourcesLayer = computed(() => {
  if (state.value.selectedJobNumber || !state.value.layerToggles.resources) {
    return true
  }
})

const hideJobClustersLayer = computed(() => {
  if (state.value.selectedJobNumber || !state.value.layerToggles.jobs) {
    return true
  }
})

onMounted(() => {
  document.title = 'Field View - IronSight'
})

onBeforeUnmount(() => {
  document.title = 'IronSight'
})

return { __sfc: true,route, router, showFeedbackDialog, jobFilterStore, navStore, isMobileView, isAdmin, showSideSheet, hiddenLocationIds, selectedLocationIds, hiddenResourceIds, state, handleLocationsClicked, handleResourcesClicked, handleJobsClustersClicked, handleNewLocationClick, hideLocationsLayer, hideResourcesLayer, hideJobClustersLayer, RightDrawer, LocationsLayer, LocationsModifiedLayer, LocationsSelectedLayer, FieldViewSearch, FieldMap, ResourcesLayer, ResourcesSelectedLayer, JobsLayer, SingleJobLayer, FieldViewHoverPopup, FeedbackDialog, LayersMenu, NewLocationMenu }
}

})